import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';
import { activationFor, isActivated } from '../utils/rateActivations';

// Per-driver unit rates — the Rates tab of the RCTI Google Sheet, in the engine.
//
// The Rates tab is what the RCTI build bills Parcels / Express / DoE / Depot Bags on
// (sheets.js → ratesMap → RCTIPage.buildRCTI). Editing here writes that tab through the
// same Apps Script `setRate` upsert the Driver Review rate editor uses (Key = "LOGIN|Type"),
// so there is still ONE rate card, not a copy of it.
//
// Every save is re-read from the server before it's called saved. The Apps Script round
// trip can land and still lose its reply (proxy deadline / HTML error page), so "no answer"
// means UNKNOWN, and the only proof is the rate card the RCTI will actually read.
//
// Drivers on DATED rates (Driver Review → Activate) are billed from their activation, not
// from this tab, so an edit here would change nothing on their RCTI. Their rates are shown
// locked, with where to change them, rather than letting a save look like it worked.

const TYPES = [
  { key: 'Parcel',     label: 'Parcel' },
  { key: 'Exp',        label: 'Express Post' },
  { key: 'DOE',        label: 'DoE' },
  { key: 'Depot Bags', label: 'Depot Bags' },
];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// 2 decimals, or 3 when the rate carries a tenth of a cent (e.g. $2.388).
const fmtNum = (v) => {
  if (v == null || v === '' || !isFinite(Number(v))) return '';
  const s = Number(v).toFixed(3);
  return s.endsWith('0') ? s.slice(0, -1) : s;
};
const fmtRate = (v) => (fmtNum(v) ? `$${fmtNum(v)}` : '');

// Rates for one login, whatever case the sheet keyed it under.
function ratesFor(ratesMap, login) {
  const k = Object.keys(ratesMap || {}).find(x => x.toUpperCase() === login);
  return k ? ratesMap[k] : {};
}

async function loadSheets() {
  const res = await fetch('/.netlify/functions/sheets');
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to load rates');
  return json;
}

const shape = (json) => ({
  drivers: json.drivers || [], ratesMap: json.ratesMap || {},
  rateActivations: json.rateActivations || {}, routeMap: json.driverRouteMap || {},
});

export default function ParcelRatesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [drafts, setDrafts] = useState({});        // { LOGIN: { Parcel: '2.90', ... } }
  const [saving, setSaving] = useState(null);       // login being saved
  const [results, setResults] = useState({});       // { LOGIN: { ok, msg } }
  const [photos, setPhotos] = useState({});
  const asked = useRef(new Set());                  // photo logins already requested

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const json = await loadSheets();
      setData(shape(json));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const today = todayISO();
    return data.drivers
      .filter(d => (d.loginName || '').trim())
      .map(d => {
        const login = d.loginName.toUpperCase().trim();
        const acts = data.rateActivations[login];
        const dated = isActivated(acts);
        const act = dated ? activationFor(acts, today) : null;
        const perParcel = (d.type || 'Parcel') === 'Parcel';
        return {
          login, perParcel, name: d.name || login, type: d.type || '', route: data.routeMap[login] || '', active: d.active !== false,
          sheet: ratesFor(data.ratesMap, login), dated, act,
        };
      })
      .sort((a, b) => a.login.localeCompare(b.login));
  }, [data]);

  const q = search.trim().toLowerCase();
  const visible = rows.filter(r => (showInactive || r.active)
    && (!q || r.login.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)));
  const noRateCount = rows.filter(r => r.active && r.perParcel && !r.dated && !(Number(r.sheet.Parcel) > 0)).length;

  const visibleKey = visible.map(r => r.login).join(',');
  useEffect(() => {
    const logins = visibleKey.split(',').filter(l => l && !asked.current.has(l));
    if (!logins.length) return;
    logins.forEach(l => asked.current.add(l));
    fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...prev, ...p }))).catch(() => {});
  }, [visibleKey]);

  const setDraft = (login, type, value) => {
    setDrafts(prev => ({ ...prev, [login]: { ...(prev[login] || {}), [type]: value } }));
    setResults(prev => { const n = { ...prev }; delete n[login]; return n; });
  };

  // The jobs a row would send: only cells that were typed in AND differ from the sheet.
  const jobsFor = (r) => {
    const d = drafts[r.login] || {};
    const jobs = [];
    for (const t of TYPES) {
      const raw = d[t.key];
      if (raw == null || String(raw).trim() === '') continue;
      const n = Number(raw);
      if (!(isFinite(n) && n >= 0)) return { error: `${t.label} must be a number, 0 or more` };
      if (n !== Number(r.sheet[t.key] ?? NaN)) jobs.push({ type: t.key, label: t.label, rate: n });
    }
    return { jobs };
  };

  async function saveRow(r) {
    const { jobs, error: bad } = jobsFor(r);
    if (bad) { setResults(prev => ({ ...prev, [r.login]: { ok: false, msg: bad } })); return; }
    if (!jobs.length) return;
    const summary = jobs.map(j => `${j.label} ${fmtRate(r.sheet[j.type]) || '(none)'} → ${fmtRate(j.rate)}`).join('\n');
    if (!window.confirm(`Update ${r.name} (${r.login}) on the Rates sheet?\n\n${summary}\n\nThe next RCTI build bills on these rates.`)) return;

    setSaving(r.login);
    try {
      for (const j of jobs) {
        let d = null;
        try {
          const res = await fetch('/.netlify/functions/appsscript', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'setRate', login: r.login, type: j.type, rate: j.rate }),
          });
          d = JSON.parse(await res.text());
        } catch { /* network error / HTML error page — outcome unknown, the read-back decides */ }
        // Only a clear "no" is a failure; a lost or timed-out reply may still have landed.
        if (d && d.success === false && !d.timedOut) throw new Error(`${j.label}: ${d.error || 'refused'}`);
      }
      // Read the rate card back: this is the same read the RCTI build bills on.
      const json = await loadSheets();
      const live = ratesFor(json.ratesMap || {}, r.login);
      const missed = jobs.filter(j => Number(live[j.type]) !== Number(j.rate));
      setData(shape(json));
      if (missed.length) {
        setResults(prev => ({ ...prev, [r.login]: { ok: false, msg: `Not on the sheet yet: ${missed.map(j => j.label).join(', ')}. Nothing else changed — try again.` } }));
      } else {
        setDrafts(prev => { const n = { ...prev }; delete n[r.login]; return n; });
        setResults(prev => ({ ...prev, [r.login]: { ok: true, msg: `Saved to the Rates sheet: ${jobs.map(j => `${j.label} ${fmtRate(j.rate)}`).join(', ')}` } }));
      }
    } catch (e) {
      setResults(prev => ({ ...prev, [r.login]: { ok: false, msg: e.message } }));
    } finally {
      setSaving(null);
    }
  }

  const cell = { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13, verticalAlign: 'middle' };
  const th = { ...cell, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' };

  return (
    <div>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#1e3a8a', lineHeight: 1.5 }}>
        These are the rates each driver is paid per unit (ex GST). Saving writes the <strong>Rates</strong> tab of the RCTI
        Google Sheet, and the next RCTI build bills on it. Leave a cell blank to keep what's there.
        An <strong>Hourly</strong> driver is paid the higher of their hours × award rate and their units × these rates, so a
        parcel rate doesn't add to their pay; set their award rate on <em>Award Rates</em> and their hours on Hourly Drivers.
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search driver or login…"
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minWidth: 240 }} />
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> Show inactive
        </label>
        {noRateCount > 0 && (
          <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309', background: '#fef3c7', borderRadius: 12, padding: '3px 10px' }}>
            ⚠ {noRateCount} active per-parcel driver{noRateCount === 1 ? '' : 's'} with no parcel rate
          </span>
        )}
        <button onClick={load} disabled={loading} style={{ marginLeft: 'auto', padding: '7px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {error && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>Couldn't load rates: {error}</div>}

      {data && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflowX: 'auto', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr>
                <th style={th}>Driver</th>
                <th style={th}>Pay type</th>
                {TYPES.map(t => <th key={t.key} style={{ ...th, textAlign: 'right' }}>{t.label}</th>)}
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const d = drafts[r.login] || {};
                const { jobs = [] } = jobsFor(r);
                const res = results[r.login];
                const missingParcel = r.perParcel && !r.dated && r.active && !(Number(r.sheet.Parcel) > 0);
                return (
                  <tr key={r.login} style={{ background: missingParcel ? '#fffbeb' : (r.active ? '#fff' : '#f9fafb'), opacity: r.active ? 1 : 0.65 }}>
                    <td style={cell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <DriverAvatar login={r.login} photo={photos[r.login]} size={28} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#111827' }}>{r.name}</div>
                          <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>{r.login}{r.route ? ` · ${r.route}` : ''}</div>
                        </div>
                      </div>
                    </td>
                    <td style={cell}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: r.type === 'Hourly' ? '#ede9fe' : '#e0f2fe', color: r.type === 'Hourly' ? '#6d28d9' : '#0369a1' }}>{r.type || 'Parcel'}</span>
                    </td>
                    {r.dated ? (
                      <>
                        {TYPES.map(t => (
                          <td key={t.key} style={{ ...cell, textAlign: 'right', color: '#374151' }}>
                            {fmtRate(r.act && r.act.rates ? r.act.rates[t.key] : undefined) || '—'}
                          </td>
                        ))}
                        <td style={{ ...cell, fontSize: 11, color: '#6d28d9', maxWidth: 220 }}>
                          🔒 Dated rates from {r.act ? r.act.effectiveFromISO : '—'}. The RCTI bills these, not the sheet — change them in Driver Review → Activate.
                        </td>
                      </>
                    ) : (
                      <>
                        {TYPES.map(t => {
                          const cur = r.sheet[t.key];
                          const v = d[t.key] ?? '';
                          const changed = v !== '' && Number(v) !== Number(cur ?? NaN);
                          return (
                            <td key={t.key} style={{ ...cell, textAlign: 'right' }}>
                              <input type="number" min="0" step="0.01" inputMode="decimal" value={v}
                                placeholder={fmtNum(cur) || '—'}
                                onChange={e => setDraft(r.login, t.key, e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && jobs.length) saveRow(r); }}
                                aria-label={`${t.label} rate for ${r.login}`}
                                style={{ width: 84, padding: '6px 8px', textAlign: 'right', fontSize: 13, borderRadius: 6,
                                  border: `1px solid ${changed ? '#f59e0b' : '#d1d5db'}`, background: changed ? '#fffbeb' : '#fff' }} />
                            </td>
                          );
                        })}
                        <td style={{ ...cell, minWidth: 180 }}>
                          {jobs.length > 0 && (
                            <button onClick={() => saveRow(r)} disabled={saving === r.login}
                              style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#1a3a6b', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                              {saving === r.login ? 'Saving…' : `Save ${jobs.length}`}
                            </button>
                          )}
                          {res && <div style={{ fontSize: 11, marginTop: 4, color: res.ok ? '#047857' : '#b91c1c' }}>{res.ok ? '✓ ' : '✗ '}{res.msg}</div>}
                          {!jobs.length && !res && missingParcel && <span style={{ fontSize: 11, color: '#b45309' }}>No parcel rate — RCTI bills $0</span>}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
              {!visible.length && (
                <tr><td colSpan={7} style={{ ...cell, textAlign: 'center', color: '#6b7280', padding: 24 }}>No drivers match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
